import React, { useEffect, useRef } from "react";
import { bool, func, instanceOf } from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik } from "formik";
import { selectors as sessionSelectors } from "reducers/session";

import { actions as debinActions } from "reducers/debin";
import { actions as accountsActions } from "reducers/accounts";

import { states, statesRecived, scheduledStatesRecived, getDebinTypes } from "util/debin";
import { getLimitDate } from "util/date";
import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import moment from "moment";

import { DateRangeField } from "pages/_components/fields/DateRangeField";
import Selector from "pages/_components/fields/formik/Selector";
import Button from "pages/_components/Button";
import * as Yup from "yup";
import TextField from "pages/_components/fields/TextField";

const FORM_ID = "debin";
const FORM_ID_RECI = "debin.received";
const FORM_ID_EMMITED = "debin.emmited";
const LIMIT_DATA = configUtils.get("debin.list.filter.defaultRange", -184);

const FilterForm = ({ customValues, closeSidebar, isEmittedForm, isReceived, isRecurrent, isEmited, isScheduled }) => {
    const formRef = useRef(null);
    const dispatch = useDispatch();
    const { filterRecurrencyList } = useSelector((state) => state.debin);
    const { accounts } = useSelector((state) => state.accounts);
    const activeEnvironment = useSelector(sessionSelectors.getActiveEnvironment);
    const debinTypes = getDebinTypes(activeEnvironment?.type);

    const statesOptions = i18n.formatOption(states, "debin.states");
    const statesOptionsReceived = i18n.formatOptionNoSpace(statesRecived, "debin.states");
    const scheduledStatesOptionsReceived = i18n.formatOptionNoSpace(scheduledStatesRecived, "scheduledDebin.states");
    const typesOptionsReceived = i18n.formatOptionLabel(debinTypes, "debin.type");

    useEffect(() => {
        dispatch(accountsActions.listAccountsCbu());
    }, isScheduled);

    const initialValues = {
        account: "TODOS",
        seller: "",
        statusRecurrent: "TODOS",
        statusEmited: "Todos",
        statusReceived: filterRecurrencyList?.status ? filterRecurrencyList.status : "TODOS",
        sellerRecived: filterRecurrencyList?.seller ? filterRecurrencyList.seller : "",
        sellerEmitted: "",
        cbu: "",
        type: "TODOS",
        ...customValues,
    };
    const receivedDebinValidation = Yup.lazy(() => {
        const validate = {
            sellerRecived: Yup.string().min(11, i18n.get("debin.must.cuit")),
            sellerEmitted: Yup.string().min(11, i18n.get("debin.must.cuit")),
            seller: Yup.string().min(11, i18n.get("debin.must.cuit")),
        };
        return Yup.object().shape(validate);
    });

    useEffect(() => {
        let requestFilters;
        if (isRecurrent) {
            requestFilters = {
                createdFrom: initialValues?.dateRangeRecurrency[0],
                createdTo: initialValues?.dateRangeRecurrency[1],
                status: initialValues.statusRecurrent,
                account: initialValues.account,
                seller: initialValues.seller,
                cbu: initialValues.cbu,
                ...filterRecurrencyList,
            };
        }
        if (isReceived || isScheduled) {
            requestFilters = {
                status: filterRecurrencyList?.statusReceived
                    ? filterRecurrencyList?.statusReceived
                    : initialValues.statusReceived,
                seller: filterRecurrencyList?.sellerRecived
                    ? filterRecurrencyList?.sellerRecived
                    : initialValues.sellerRecived,
                account: filterRecurrencyList?.account ? filterRecurrencyList?.account : initialValues.account,
                ...filterRecurrencyList,
            };
            if (isReceived) {
                requestFilters = {
                    dateFrom: filterRecurrencyList?.dateRange
                        ? filterRecurrencyList?.dateRange[0]
                        : initialValues.dateRange[0],
                    dateTo: filterRecurrencyList?.dateRange
                        ? filterRecurrencyList?.dateRange[1]
                        : initialValues.dateRange[1],
                    ...requestFilters,
                };
            }
            if (isScheduled) {
                requestFilters = {
                    dateFrom: filterRecurrencyList?.dateRangeScheduled
                        ? filterRecurrencyList?.dateRangeScheduled[0]
                        : initialValues.dateRangeScheduled[0],
                    dateTo: filterRecurrencyList?.dateRangeScheduled
                        ? filterRecurrencyList?.dateRangeScheduled[1]
                        : initialValues.dateRangeScheduled[1],
                    ...requestFilters,
                };
            }
        }
        if (isEmited) {
            requestFilters = {
                dateFrom: initialValues.dateRange[0],
                dateTo: initialValues.dateRange[1],
                status: initialValues.statusEmited,
                seller: initialValues.sellerEmitted,
                ...filterRecurrencyList,
            };
        }
        dispatch(debinActions.setRecurrencyDebinFilters({ ...requestFilters, pageNumber: 1 }));
    }, []);

    useEffect(() => {
        const filtersTags = Object.entries(filterRecurrencyList);
        filtersTags.forEach((item) => {
            if (item[0] === "dateFrom") {
                formRef.current.setFieldValue( isScheduled ? "dateRangeScheduled[0]" : "dateRange[0]", item[1]);
            }
            if (item[0] === "createdFrom") {
                formRef.current.setFieldValue("dateRangeRecurrency[0]", item[1]);
            }
            formRef.current.setFieldValue(item[0], item[1]);
        });
    }, [filterRecurrencyList]);

    const accountList = accounts.map((account) => ({
        cbu: account?.cbu?.slice(9),
        value: account?.productId,
        label: `${i18n.get(`productType.${account?.accountType}`)} ····${account?.accountNumber?.slice(-4)} ${i18n.get(
            `currency.label.${account?.currency}`,
        )}`,
    }));

    const handleSubmit = (values) => {
        const account = accounts.find((a) => a?.productId === values?.account);

        let statusToSearch;
        if (values.statusRecurrent === "ACTIVO") {
            statusToSearch = "ACTIVA";
        }
        if (values.statusRecurrent === "PAUSADO") {
            statusToSearch = "INACTIVA";
        }
        if (values.statusRecurrent === "ELIMINADO") {
            statusToSearch = "INACTIVA DEFINITIVA";
        }
        if (values.statusRecurrent === "TODOS") {
            statusToSearch = "TODOS";
        }
        let requestFilters;
        if (isRecurrent) {
            requestFilters = {
                ...filterRecurrencyList,
                account: values.account,
                status: values.statusRecurrent,
                createdFrom: values.dateRangeRecurrency[0],
                createdTo: values.dateRangeRecurrency[1],
                seller: values.seller,
                cbu: account?.cbu?.slice(9) || "",
            };
        }
        if (isReceived || isScheduled) {
            requestFilters = {
                ...filterRecurrencyList,
                status: values.statusReceived,
                dateFrom: isScheduled ? values.dateRangeScheduled[0] : values.dateRange[0],
                dateTo: isScheduled ? values.dateRangeScheduled[1] : values.dateRange[1],
                seller: values.sellerRecived,
                account: values.account,
            };
        }
        if (isEmited) {
            requestFilters = {
                ...filterRecurrencyList,
                status: values.statusEmited,
                dateFrom: values.dateRange[0],
                dateTo: values.dateRange[1],
                seller: values.sellerEmitted,
                recipient: values.sellerEmitted,
                recipientType: "comprador",
            };
        }
        dispatch(debinActions.setRecurrencyDebinFilters(requestFilters));
        if (isRecurrent) {
            dispatch(
                debinActions.getDebinRecurrencies({
                    cbu: account?.cbu?.slice(9) || "",
                    account: values.account,
                    pageNumber: 1,
                    seller: values.seller,
                    status: statusToSearch,
                    createdFrom: values.dateRangeRecurrency[0],
                    createdTo: values.dateRangeRecurrency[1],
                }),
            );
        }
        if (isReceived) {
            dispatch(
                debinActions.loadIncomingRequests({
                    dateFrom: values.dateRange[0],
                    dateTo: values.dateRange[1],
                    seller: values.sellerRecived,
                    status: values.statusReceived,
                    tipo: values.type == "TODOS" ? null : values.type,
                    pageNumber: filterRecurrencyList.pageNumber,
                }),
            );
        }
        if (isEmited) {
            dispatch(
                debinActions.loadSentRequests({
                    dateFrom: values.dateRange[0],
                    dateTo: values.dateRange[1],
                    status: values.statusEmited,
                    pageNumber: filterRecurrencyList.pageNumber,
                    recipient: values.sellerEmitted,
                    recipientType: "comprador",
                }),
            );
        }
        if (isScheduled) {
            dispatch(
                debinActions.scheduledDebinList({
                    dateFrom: values.dateRangeScheduled[0],
                    dateTo: values.dateRangeScheduled[1],
                    seller: values.sellerRecived,
                    status: values.statusReceived,
                    cbu: account?.cbu?.slice(11) || "",
                    pageNumber: filterRecurrencyList.pageNumber,
                }),
            );
        }
        closeSidebar((state) => !state);
    };

    const handleReset = (setFieldValue) => {
        setFieldValue("account", "");
        setFieldValue("seller", "");
        setFieldValue("sellerRecived", "");
        setFieldValue("sellerEmitted", "");
        setFieldValue("statusRecurrent", "TODOS");
        setFieldValue("statusReceived", "Todos");
        setFieldValue("statusEmited", "Todos");
        setFieldValue("dateRange", [getLimitDate(LIMIT_DATA), moment().toDate()]);
        setFieldValue("dateRangeScheduled", [getLimitDate(LIMIT_DATA), moment().toDate()]);
        setFieldValue("dateRangeRecurrency", [getLimitDate(LIMIT_DATA), getLimitDate(7)]);
    };

    const AccountArray = [{ value: "TODOS", label: "TODAS", cbu: "" }, ...accountList];

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            ref={formRef}
            validationSchema={receivedDebinValidation}>
            {({ setFieldValue }) => (
                <Form>
                    <Row>
                        {isRecurrent && (
                            <>
                                <Col xs={12} md={6}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="account"
                                        component={Selector}
                                        options={AccountArray}
                                        placeholder={i18n.get("debin.placeholder.todas")}
                                    />
                                </Col>
                                {isEmittedForm && (
                                    <Col xs={12} md={6}>
                                        <Field
                                            component={TextField}
                                            placeholderText={i18n.get("debin.seller.label")}
                                            idForm={FORM_ID}
                                            name="seller"
                                            type="text"
                                            pattern="[0-9]*"
                                            maxLength={11}
                                            minLength={0}
                                        />
                                    </Col>
                                )}
                                <Col xs={12} md={6}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="statusRecurrent"
                                        component={Selector}
                                        options={statesOptions}
                                        placeholder={i18n.get("debin.placeholder.todos")}
                                    />
                                </Col>
                                <Col xs={12} md={6}>
                                    <Field
                                        component={DateRangeField}
                                        hidePlaceholder
                                        idForm={FORM_ID}
                                        minDate={getLimitDate(LIMIT_DATA)}
                                        maxDate={getLimitDate(99999)}
                                        name="dateRangeRecurrency"
                                        dateFormat="dd/MM/yyyy"
                                        selectsRange
                                        showMonthYearDropdown
                                        scrollableMonthYearDropdown
                                    />
                                </Col>
                            </>
                        )}
                        {(isReceived || isScheduled) && (
                            <>
                                {isEmittedForm && (
                                    <Col xs={12} md={6}>
                                        <Field
                                            component={TextField}
                                            placeholderText={i18n.get("debin.received.sellerRecived.label")}
                                            idForm={FORM_ID_RECI}
                                            name="sellerRecived"
                                            type="text"
                                            pattern="[0-9]*"
                                            maxLength={11}
                                            minLength={0}
                                        />
                                    </Col>
                                )}
                                <Col xs={12} md={6}>
                                    <Field
                                        idForm={FORM_ID_RECI}
                                        name="statusReceived"
                                        component={Selector}
                                        options={isScheduled ? scheduledStatesOptionsReceived : statesOptionsReceived}
                                    />
                                </Col>
                                <Col xs={12} md={6}>
                                    <Field
                                        component={DateRangeField}
                                        hidePlaceholder
                                        idForm={FORM_ID_RECI}
                                        minDate={getLimitDate(LIMIT_DATA)}
                                        maxDate={getLimitDate(0)}
                                        name={isScheduled ? "dateRangeScheduled" : "dateRange"}
                                        dateFormat="dd/MM/yyyy"
                                        selectsRange
                                        showMonthYearDropdown
                                        scrollableMonthYearDropdown
                                    />
                                </Col>

                                {isScheduled && (
                                    <Col xs={12} md={6}>
                                        <Field
                                            idForm={FORM_ID}
                                            name="account"
                                            component={Selector}
                                            options={AccountArray}
                                            placeholder={i18n.get("debin.placeholder.todas")}
                                        />
                                    </Col>
                                )}
                            </>
                        )}
                        {isReceived && (
                            <Col xs={12} md={6}>
                                <Field
                                    idForm={FORM_ID_RECI}
                                    name="type"
                                    component={Selector}
                                    options={typesOptionsReceived}
                                />
                            </Col>
                        )}

                        {isEmited && (
                            <>
                                {isEmittedForm && (
                                    <Col xs={12} md={6}>
                                        <Field
                                            component={TextField}
                                            placeholderText={i18n.get("debin.received.sellerEmitted.label")}
                                            idForm={FORM_ID_RECI}
                                            name="sellerEmitted"
                                            type="text"
                                            pattern="[0-9]*"
                                            maxLength={11}
                                            minLength={0}
                                        />
                                    </Col>
                                )}
                                <Col xs={12} md={6}>
                                    <Field
                                        idForm={FORM_ID_RECI}
                                        name="statusEmited"
                                        component={Selector}
                                        options={statesOptionsReceived}
                                        placeholder={i18n.get("debin.placeholder.todas")}
                                    />
                                </Col>
                                <Col xs={12} md={6}>
                                    <Field
                                        component={DateRangeField}
                                        hidePlaceholder
                                        idForm={FORM_ID_EMMITED}
                                        minDate={getLimitDate(LIMIT_DATA)}
                                        maxDate={getLimitDate(99999)}
                                        name="dateRange"
                                        dateFormat="dd/MM/yyyy"
                                        selectsRange
                                        showMonthYearDropdown
                                        scrollableMonthYearDropdown
                                    />
                                </Col>
                            </>
                        )}
                        {}
                    </Row>
                    <Row className="form-actions">
                        <Col xs={12} md={6}>
                            <Button
                                className="btn-outline"
                                label="echecks.clean.filters"
                                onClick={() => handleReset(setFieldValue)}
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <Button type="submit" className="btn-primary" label="echecks.filtering" />
                        </Col>
                    </Row>
                </Form>
            )}
        </Formik>
    );
};

FilterForm.propTypes = {
    customValues: instanceOf(Object).isRequired,
    closeSidebar: func.isRequired,
    isEmittedForm: bool,
    isReceived: bool,
    isRecurrent: bool,
    isEmited: bool,
    isScheduled: bool,
};

FilterForm.defaultProps = {
    isEmittedForm: false,
    isReceived: false,
    isRecurrent: false,
    isEmited: false,
    isScheduled: false,
};

export default FilterForm;
